import React from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Car, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { BIZ, GOOGLE_MAPS_PLACE_URL } from "../lib/constants";
import { StorePhoto, storePhotoUrl } from "../components/StorePhoto";

const jsonLd = {
    "@context": "https://schema.org",
    "@type": ["LiquorStore", "LocalBusiness"],
    name: BIZ.name,
    image: `${BIZ.siteUrl}/api/static/uploads/store/og-storefront.jpg`,
    url: `${BIZ.siteUrl}/visit`,
    telephone: BIZ.phone,
    email: BIZ.email,
    priceRange: "$$",
    sameAs: [
        BIZ.socials.facebook,
        BIZ.socials.instagram,
        BIZ.socials.twitter,
        BIZ.socials.tiktok,
        BIZ.socials.youtube,
        GOOGLE_MAPS_PLACE_URL,
    ],
    address: {
        "@type": "PostalAddress",
        streetAddress: BIZ.address.line1,
        addressLocality: BIZ.address.city,
        addressRegion: BIZ.address.state,
        postalCode: BIZ.address.zip,
        addressCountry: BIZ.address.country,
    },
    geo: {
        "@type": "GeoCoordinates",
        latitude: BIZ.geo.lat,
        longitude: BIZ.geo.lng,
    },
    openingHoursSpecification: BIZ.hoursSpec.map((s) => ({
        "@type": "OpeningHoursSpecification",
        dayOfWeek: s.days,
        opens: s.opens,
        closes: s.closes,
    })),
};

export default function Visit() {
    return (
        <>
            <SEO
                title="Visit Us"
                description="Visit Liquor Studio at 309 S 2nd St, Clinton, MO. Map, hours, phone, and directions for the premier liquor store serving Henry County and Truman Lake."
                path="/visit"
                image={storePhotoUrl("og-storefront", "jpg")}
                jsonLd={jsonLd}
            />

            {/* Storefront night photo as hero band */}
            <section
                aria-label="Storefront"
                data-testid="visit-hero-photo"
                style={{ position: "relative", marginTop: 0, paddingTop: 72 }}
            >
                <StorePhoto
                    slug="storefront-night"
                    loading="eager"
                    style={{ width: "100%", height: "min(54vh, 520px)" }}
                    testid="visit-storefront-photo"
                />
                <div style={{ position: "absolute", inset: "72px 0 0 0", background: "linear-gradient(180deg, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.85) 100%)", pointerEvents: "none" }} aria-hidden="true" />
                <div style={{ position: "absolute", left: 0, right: 0, bottom: 0 }}>
                    <div className="ls-container" style={{ padding: "0 max(1.5rem, 5vw) 32px" }}>
                        <span className="eyebrow" style={{ color: "#F5B027" }}>Visit Us</span>
                        <h1 className="font-display text-white mt-3" style={{ fontSize: "clamp(40px, 6vw, 72px)", fontWeight: 500, lineHeight: 1.02, letterSpacing: "-0.02em", textShadow: "0 2px 12px rgba(0,0,0,0.8)" }}>
                            309 S 2nd Street, <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>Clinton</em>.
                        </h1>
                    </div>
                </div>
            </section>

            <section
                style={{ paddingTop: 160, paddingBottom: 64, borderBottom: "1px solid var(--ls-border)" }}
                data-testid="visit-header"
            >
                <div className="ls-container">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <span className="eyebrow">Visit Us</span>
                        <h1
                            className="font-display mt-5 text-white"
                            style={{
                                fontSize: "clamp(44px, 7vw, 84px)",
                                fontWeight: 500,
                                lineHeight: 1.02,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Come find us<br />
                            <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                                on 2nd Street.
                            </em>
                        </h1>
                        <p
                            className="mt-6"
                            style={{
                                color: "var(--ls-text-secondary)",
                                fontSize: 18,
                                lineHeight: 1.7,
                                maxWidth: 620,
                            }}
                        >
                            Just off MO-7 & MO-13, two minutes from downtown Clinton Square.
                            Easy parking. Easy hours. Real recommendations.
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="section-y" data-testid="visit-content">
                <div className="ls-container">
                    {/* Map */}
                    <div
                        style={{
                            borderRadius: 6,
                            overflow: "hidden",
                            border: "1px solid var(--ls-border-strong)",
                            aspectRatio: "16/8",
                            marginBottom: 64,
                        }}
                    >
                        <iframe
                            title={`Map: ${BIZ.name}, ${BIZ.address.full}`}
                            src={`https://www.google.com/maps?q=${encodeURIComponent(BIZ.address.full)}&output=embed`}
                            width="100%"
                            height="100%"
                            style={{ border: 0, display: "block" }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            data-testid="visit-map-iframe"
                        />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                        {/* Address */}
                        <article
                            style={{
                                background: "var(--ls-panel)",
                                border: "1px solid var(--ls-border)",
                                borderRadius: 6,
                                padding: 32,
                            }}
                            data-testid="visit-card-address"
                        >
                            <MapPin size={22} style={{ color: "var(--ls-gold)" }} />
                            <h2 className="font-display mt-4 text-white" style={{ fontSize: 24, fontWeight: 500 }}>
                                Address
                            </h2>
                            <p className="mt-3 text-white" style={{ fontSize: 16, lineHeight: 1.6 }}>
                                {BIZ.address.line1}<br />
                                {BIZ.address.city}, {BIZ.address.state} {BIZ.address.zip}
                            </p>
                            <a
                                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(BIZ.address.full)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-outline-gold mt-6"
                                style={{ fontSize: 13, padding: "10px 18px" }}
                                data-testid="visit-directions-cta"
                            >
                                Get Directions <ArrowRight size={14} />
                            </a>
                        </article>

                        {/* Phone */}
                        <article
                            style={{
                                background: "var(--ls-panel)",
                                border: "1px solid var(--ls-border)",
                                borderRadius: 6,
                                padding: 32,
                            }}
                            data-testid="visit-card-phone"
                        >
                            <Phone size={22} style={{ color: "var(--ls-gold)" }} />
                            <h2 className="font-display mt-4 text-white" style={{ fontSize: 24, fontWeight: 500 }}>
                                Call Us
                            </h2>
                            <p className="mt-3 text-white" style={{ fontSize: 16 }}>
                                Looking for something specific? Give us a ring.
                            </p>
                            <a
                                href={`tel:${BIZ.phoneTel}`}
                                className="btn-primary mt-6"
                                style={{ fontSize: 14, padding: "12px 22px" }}
                                data-testid="visit-phone-cta"
                            >
                                {BIZ.phone}
                            </a>
                        </article>

                        {/* Parking */}
                        <article
                            style={{
                                background: "var(--ls-panel)",
                                border: "1px solid var(--ls-border)",
                                borderRadius: 6,
                                padding: 32,
                            }}
                            data-testid="visit-card-parking"
                        >
                            <Car size={22} style={{ color: "var(--ls-gold)" }} />
                            <h2 className="font-display mt-4 text-white" style={{ fontSize: 24, fontWeight: 500 }}>
                                Parking
                            </h2>
                            <p className="mt-3" style={{ color: "var(--ls-text-secondary)", fontSize: 15, lineHeight: 1.6 }}>
                                Free off-street parking in our private lot.
                                Towing your boat? We've got room.
                            </p>
                        </article>
                    </div>

                    {/* Hours table */}
                    <div
                        className="mt-16"
                        style={{
                            background: "var(--ls-panel)",
                            border: "1px solid var(--ls-border)",
                            borderRadius: 6,
                            padding: 40,
                        }}
                        data-testid="visit-hours-table"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <Clock size={22} style={{ color: "var(--ls-gold)" }} />
                            <h2 className="font-display text-white" style={{ fontSize: 28, fontWeight: 500 }}>
                                Hours
                            </h2>
                        </div>
                        <table className="w-full text-left" style={{ borderCollapse: "collapse" }}>
                            <caption className="sr-only">Liquor Studio store hours</caption>
                            <tbody>
                                {BIZ.hours.map((h) => (
                                    <tr
                                        key={h.day}
                                        style={{ borderTop: "1px solid var(--ls-border)" }}
                                    >
                                        <th
                                            scope="row"
                                            className="font-body py-3 pr-4 text-white"
                                            style={{ fontWeight: 500, fontSize: 15 }}
                                        >
                                            {h.day}
                                        </th>
                                        <td
                                            className="py-3 text-right"
                                            style={{ color: "var(--ls-text-secondary)", fontSize: 15 }}
                                        >
                                            {h.open} — {h.close}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p
                            className="mt-6 text-xs"
                            style={{ color: "var(--ls-text-muted)", lineHeight: 1.6 }}
                        >
                            Holiday hours may vary. We ID anyone who appears under 40.
                            Missouri law requires the buyer to be 21 or older.
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
